import { cloneDeep } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';

import {
  ClientPresentationShape,
  ClientPresentationV2Configuration,
} from '../../ClientPresentationDesigner.types';
import { ClientPresentationV2Bundle } from '../../types/ClientPresentation.PresentationBundleType';

/** Form-shaped state that the server holds or a save has sent. */
export interface PresentationSaveState {
  bundles: ClientPresentationV2Bundle[];
  presentationConfiguration: ClientPresentationV2Configuration;
  title: ClientPresentationShape['title'];
}

export interface PresentationSaveStateHandle {
  /** Null until the form has been loaded, which is when saving may begin. */
  read: () => PresentationSaveState | null;
  /** Called when the form is (re)loaded from the server. */
  seed: (next: PresentationSaveState) => void;
  /** Advances before send to prevent duplicate saves across renders. */
  advance: (next: PresentationSaveState) => void;
  /** Stops saving after an incomplete cycle until a fresh read seeds state. */
  invalidate: () => void;
}

/** Disables saving on read-only surfaces and outside the provider. */
export const NEVER_SEEDED_SAVE_STATE: PresentationSaveStateHandle = {
  read: () => null,
  seed: () => undefined,
  advance: () => undefined,
  invalidate: () => undefined,
};

/**
 * Tracks form-shaped server state so each save is independent of prior renders.
 * Raw responses omit transient form fields and would always appear different.
 */
export function usePresentationSaveState(): PresentationSaveStateHandle {
  const stateRef = useRef<PresentationSaveState | null>(null);

  const read = useCallback(() => stateRef.current, []);
  const write = useCallback((next: PresentationSaveState) => {
    // RHF can mutate watched objects in place. Copy them so saved state remains
    // stable while the form changes.
    stateRef.current = {
      bundles: cloneDeep(next.bundles),
      presentationConfiguration: { ...next.presentationConfiguration },
      title: next.title,
    };
  }, []);

  const invalidate = useCallback(() => {
    stateRef.current = null;
  }, []);

  // Separate names make load-time and save-time writes clear at call sites.
  return useMemo(
    () => ({ read, seed: write, advance: write, invalidate }),
    [read, write, invalidate]
  );
}
